import React, { useEffect, useRef, useState } from 'react';
import Section from './Section';
import ExperienceGraph from './ExperienceGraph';
import FlowDiagram from './FlowDiagram';
import { EXPERIENCE } from '../data/experience';
import { DIAGRAMS } from '../data/diagrams';
import { roleVar } from './graphPalette';

function Links({ item }) {
  if (!item.links?.length) return null;
  return (
    <div className="card__links">
      {item.links.map((l) => <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" className="btn btn--sm btn--primary">{l.label}</a>)}
    </div>
  );
}

function Detail({ item }) {
  if (!item) return null;
  return (
    <div className="xp__detail" key={item.id} id={`exp-${item.id}`}>
      <div className="xp__when"><b>{item.period}</b>{item.location}</div>
      <h3 className="xp__company">{item.company}</h3>
      <p className="xp__role">{item.role}</p>
      <div className="tags">{item.tags.map((t) => <span className="tag" key={t}>{t}</span>)}</div>
      <ul className="bullets">{item.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
      <Links item={item} />
    </div>
  );
}

/** Scroll progress (0..1) through a section element. */
function useSectionProgress(ref) {
  const [p, setP] = useState(0);
  useEffect(() => {
    let raf = 0;
    const calc = () => {
      raf = 0;
      const el = ref.current; if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const start = vh * 0.7, end = vh * 0.3;
      const total = r.height + (start - end);
      const done = start - r.top;
      setP(Math.max(0, Math.min(1, done / total)));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(calc); };
    calc();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [ref]);
  return p;
}

export default function Experience() {
  const [selected, setSelected] = useState(EXPERIENCE[0].id);
  const [view, setView] = useState('graph');
  const current = EXPERIENCE.find((e) => e.id === selected);
  const bodyRef = useRef(null);
  const progress = useSectionProgress(bodyRef);
  const reached = Math.min(EXPERIENCE.length, Math.floor(progress * (EXPERIENCE.length + 0.5)));

  return (
    <Section id="experience" num="01" title="Experience" subtitle="Where I've built, shipped, and learned — each role linked to the stack it ran on">
      <div className="xp__body" ref={bodyRef}>
        <div className="xp__rail" aria-hidden="true">
          <div className="xp__rail-fill" style={{ height: `${progress * 100}%` }} />
          {EXPERIENCE.map((e, i) => (
            <button key={e.id} className={`xp__rail-dot${i < reached ? ' on' : ''}${selected === e.id ? ' sel' : ''}`}
              style={{ top: `${((i + 0.5) / EXPERIENCE.length) * 100}%`, '--c': roleVar(i) }} onClick={() => setSelected(e.id)} title={e.company} tabIndex={-1} />
          ))}
        </div>

        <div className="xp__toolbar">
          <div className="xp__roles">
            {EXPERIENCE.map((e, i) => (
              <button key={e.id} className={`xp__pill${selected === e.id ? ' xp__pill--active' : ''}`} style={{ '--c': roleVar(i) }} onClick={() => setSelected(e.id)}>
                <i />
                {e.company}
              </button>
            ))}
          </div>
          <div className="xp__view">
            <button className={view === 'graph' ? 'on' : ''} onClick={() => setView('graph')}>Graph</button>
            <button className={view === 'list' ? 'on' : ''} onClick={() => setView('list')}>List</button>
          </div>
        </div>

        {view === 'graph' ? (
          <>
            <div className="xp__layout">
              <ExperienceGraph items={EXPERIENCE} selectedId={selected} onSelect={setSelected} reached={reached} />
              <Detail item={current} />
            </div>
            {DIAGRAMS[current.id] && <div className="xp__diagram" key={current.id}><FlowDiagram diagram={DIAGRAMS[current.id]} /></div>}
          </>
        ) : (
          <div className="xp">
            {EXPERIENCE.map((item) => (
              <div className="xp__item" key={item.id} id={`exp-${item.id}`}>
                <div className="xp__when"><b>{item.period}</b>{item.location}</div>
                <div>
                  <h3 className="xp__company">{item.company}</h3>
                  <p className="xp__role">{item.role}</p>
                  <div className="tags">{item.tags.map((t) => <span className="tag" key={t}>{t}</span>)}</div>
                  <ul className="bullets">{item.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
                  {DIAGRAMS[item.id] && <FlowDiagram diagram={DIAGRAMS[item.id]} />}
                  <Links item={item} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}
